import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { X, Globe } from "lucide-react";
import { SUPPORTED_LANGUAGES, type SupportedLanguage } from "@/i18n";

const HINT_DISMISSED_KEY = "ii_lang_hint_dismissed";

function getDismissedHint(): string | null {
  try {
    return localStorage.getItem(HINT_DISMISSED_KEY);
  } catch {
    return null;
  }
}

function dismissHint(locale: string) {
  try {
    localStorage.setItem(HINT_DISMISSED_KEY, locale);
  } catch {
    /* ignore */
  }
}

function detectBrowserLocale(): SupportedLanguage | null {
  if (typeof navigator === "undefined") return null;
  const langs = (navigator.languages || [navigator.language || ""]).filter(Boolean);
  for (const lang of langs) {
    const lower = lang.toLowerCase();
    if (lower.startsWith("de")) return "de";
    if (lower.startsWith("ja")) return "ja";
  }
  return null;
}

const LABELS: Record<SupportedLanguage, string> = {
  en: "English",
  ja: "日本語",
  de: "Deutsch",
};

const HINT_TEXT: Record<SupportedLanguage, { message: string; action: string }> = {
  en: { message: "", action: "" }, // not used
  de: {
    message: "Erscheint diese Seite auf Deutsch übersetzt?",
    action: "Auf Deutsch anzeigen",
  },
  ja: {
    message: "このページを日本語で表示しますか？",
    action: "日本語に切り替える",
  },
};

const LanguageHint: React.FC = () => {
  const { i18n } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [detectedLocale, setDetectedLocale] = useState<SupportedLanguage | null>(null);

  useEffect(() => {
    const browserLocale = detectBrowserLocale();
    if (!browserLocale) return;

    const currentLang = i18n.language?.startsWith("ja")
      ? "ja"
      : i18n.language?.startsWith("de")
        ? "de"
        : "en";

    if (currentLang === browserLocale) return;

    const dismissed = getDismissedHint();
    if (dismissed === browserLocale) return;

    setDetectedLocale(browserLocale);
    setVisible(true);
  }, [i18n.language]);

  const handleSwitch = () => {
    if (!detectedLocale) return;
    try {
      localStorage.setItem("ii_lang", detectedLocale);
    } catch {
      /* ignore */
    }
    void i18n.changeLanguage(detectedLocale);
    setVisible(false);
  };

  const handleDismiss = () => {
    if (!detectedLocale) return;
    dismissHint(detectedLocale);
    setVisible(false);
  };

  if (!visible || !detectedLocale) return null;

  const hint = HINT_TEXT[detectedLocale];

  return (
    <div
      role="alert"
      className="animate-slide-fade-in flex items-center gap-3 rounded-lg border border-brand-green/40 bg-brand-green/10 backdrop-blur-sm px-4 py-2.5 text-sm"
    >
      <Globe className="h-4 w-4 text-brand-green shrink-0" aria-hidden="true" />
      <span className="text-foreground font-sans">{hint.message}</span>
      <button
        onClick={handleSwitch}
        className="ml-auto shrink-0 rounded-md bg-brand-green/20 px-3 py-1 text-brand-green hover:bg-brand-green/30 transition-colors font-sans whitespace-nowrap"
      >
        {hint.action}
      </button>
      <button
        onClick={handleDismiss}
        className="shrink-0 rounded-md p-1 text-muted-foreground hover:text-foreground transition-colors"
        aria-label="Dismiss language hint"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};

export default LanguageHint;
